import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function LoginBarChart({ data, barFill, emptyMessage }) {
  const chartData = (data || []).map((row) => ({
    name: row.user_name || "User",
    logins: Number(row.login_count) || 0,
  }));

  if (!chartData.length) {
    return <p className="text-center text-muted mb-0 py-4">{emptyMessage}</p>;
  }

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-22}
            textAnchor="end"
            interval={0}
            height={72}
            tick={{ fontSize: 11 }}
          />
          <YAxis allowDecimals={false} />
          <Tooltip formatter={(value) => [`${value} logins`, "Sessions"]} />
          <Bar dataKey="logins" name="Logins" fill={barFill} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function UserLoginFrequencyCharts({ stats, loading, monthLabel }) {
  const tableReady = stats?.table_ready !== false;
  const most = Array.isArray(stats?.most_active) ? stats.most_active : [];
  const least = Array.isArray(stats?.least_active) ? stats.least_active : [];
  const scopeTeam = stats?.scope === "team";
  const scopeHint = scopeTeam
    ? "your direct reports"
    : "active users (excluding super admins)";

  if (loading) {
    return (
      <div className="row g-3">
        <div className="col-md-6">
          <Card className="custom-card mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-0">Loading login activity...</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="custom-card mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-0">Loading login activity...</p>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 px-1">
        <div>
          <h5 className="fw-semibold mb-1">User login activity</h5>
          <p className="text-muted mb-0 small">
            Session counts for {monthLabel || "selected month"} ({scopeHint}).
          </p>
        </div>
        <Link to="/users/session-logs" className="btn btn-sm btn-outline-primary">
          {scopeTeam ? "View team login activity" : "View all login activity"}
        </Link>
      </div>

      {!tableReady && (
        <p className="text-warning small px-1">
          Login session tracking is not available yet. Run session log setup or backfill from Users → Login activity.
        </p>
      )}

      <div className="row g-3">
        <div className="col-md-6">
          <Card className="custom-card mb-0 h-100 border-success border-opacity-25">
            <CardBody>
              <h6 className="fw-semibold text-success mb-3">Most frequent logins</h6>
              <LoginBarChart
                data={most}
                barFill="#198754"
                emptyMessage="No login sessions recorded for this month."
              />
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="custom-card mb-0 h-100 border-warning border-opacity-25">
            <CardBody>
              <h6 className="fw-semibold text-warning mb-3">Least frequent logins</h6>
              <LoginBarChart
                data={least}
                barFill="#fd7e14"
                emptyMessage="No users to rank for this month."
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
